import { Car, Home as HomeIcon, Building, CalendarClock, DollarSign, ShieldCheck, MapPin, ClipboardList } from "lucide-react";
import { DISCLOSURES, SERVICE_AREA, IMAGES } from "@/config/site";
import { useSeo } from "@/lib/seo";
import { PageHero } from "@/components/common/PageHero";
import { Section, SectionHeading } from "@/components/common/Section";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/common/Reveal";
import { BookingButton } from "@/components/common/BookingButton";
import { EmptyState } from "@/components/common/EmptyState";
import { CTABand } from "@/components/common/CTABand";

const LOCATIONS = [
  { icon: HomeIcon, title: "Homes", text: "Relax and recover in the comfort of your own space." },
  { icon: Building, title: "Businesses", text: "On-site sessions for teams and busy professionals." },
  { icon: MapPin, title: "Approved event locations", text: "Massage moments for gatherings and activations." },
];

const EXPECT = [
  "A licensed, professional massage therapist arrives prepared for your session.",
  "A calm, respectful experience tailored to your comfort and goals.",
  "Setup and space requirements are confirmed with you in advance.",
  "Clear communication before, during and after your appointment.",
];

const PLACEHOLDERS = [
  { icon: MapPin, title: "Service Area", text: "Specific coverage within " + SERVICE_AREA + " will be confirmed at booking." },
  { icon: DollarSign, title: "Travel Fee", text: "Travel-fee details will be confirmed through Vagaro." },
  { icon: CalendarClock, title: "Availability", text: "Mobile availability will be confirmed through Vagaro." },
  { icon: ShieldCheck, title: "Safety & Preparation", text: "Preparation and safety guidance will be shared when you book." },
];

export default function MobileMassage() {
  useSeo({
    title: "Mobile Massage in Greater Houston | Legacy Spa",
    description:
      "Legacy Spa mobile massage brings professional massage therapy to eligible homes, businesses and approved locations across Greater Houston.",
    path: "/mobile-massage",
  });

  return (
    <>
      <PageHero
        eyebrow="Mobile Massage"
        titleLines={["Wellness that", "comes to you."]}
        lead={`Professional massage delivered to eligible homes, businesses and approved locations across ${SERVICE_AREA}.`}
        image={IMAGES.therapistHands}
        imageAlt="Massage therapist working along a client's back and shoulders."
      />

      <Section className="bg-legacy-charcoal">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Greater Houston"
              title="Where we bring the experience."
              as="h2"
            />
            <div className="mt-8">
              <BookingButton size="lg" label="Book Mobile Massage" testId="mobile-book-btn" />
              <p className="mt-4 text-sm text-white/45">{DISCLOSURES.booking}</p>
            </div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <StaggerGroup className="grid gap-5">
              {LOCATIONS.map((l) => (
                <StaggerItem key={l.title}>
                  <div className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-legacy-mint/10 text-legacy-mint">
                      <l.icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="font-heading text-2xl text-white">{l.title}</h3>
                      <p className="mt-1 text-sm text-white/60">{l.text}</p>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </Section>

      <Section className="bg-[#12201d]">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHeading eyebrow="What to Expect" title="A seamless visit." as="h2" />
            <ul className="mt-8 space-y-4">
              {EXPECT.map((e, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="flex items-start gap-3 text-white/70">
                    <ClipboardList className="mt-0.5 h-5 w-5 shrink-0 text-legacy-mint" aria-hidden="true" />
                    <span>{e}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div>
            <SectionHeading eyebrow="Good to Know" title="Details confirmed at booking." as="h2" />
            <StaggerGroup className="mt-8 grid gap-4 sm:grid-cols-2">
              {PLACEHOLDERS.map((p) => (
                <StaggerItem key={p.title}>
                  <div className="h-full rounded-2xl border border-dashed border-white/15 bg-white/[0.02] p-5">
                    <p.icon className="h-5 w-5 text-legacy-gold" aria-hidden="true" />
                    <h3 className="mt-3 font-heading text-xl text-white">{p.title}</h3>
                    <p className="mt-1.5 text-sm text-white/55">{p.text}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </Section>

      <CTABand
        eyebrow="On your schedule"
        title="Book mobile massage today."
        lead="Location and access details are confirmed when you book through Vagaro."
      />
    </>
  );
}
